<script setup>
import {computed, onMounted, ref} from 'vue'
import { useProjectStore } from '@/stores/project'
import { useBoundaryStore } from '@/stores/boundary'
import {Folder, ImageUp, Trash2} from 'lucide-vue-next'

import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Spinner} from '@/components/ui/spinner'
import {toast} from '@/components/ui/toast/use-toast'
import {mediaConfig} from '@/config/media'
import {buildStaticMapUrl} from '@/utils/mapbox/staticMap'

const MAX_BYTES = 2 * 1024 * 1024
const MIN_WIDTH = 640
const MIN_HEIGHT = 360

const props = defineProps({
  project: {
    type: Object,
    required: true
  },
  disabled: Boolean
})

const projectStore = useProjectStore()

const boundaryStore = useBoundaryStore()
const fileInput = ref(null)
const inProgress = ref(false)
const boundary = ref(null)
const accept = mediaConfig.formats.image.extensions.map(ext => `.${ext}`).join(',')

const coverType = computed(() => props.project.cover_type || 'map')
const mapUrl = computed(() => boundary.value ? buildStaticMapUrl(boundary.value) : null)

onMounted(async () => {
  const features = await boundaryStore.fetchBoundariesByIds([props.project.project_id])
  boundary.value = features[0] || null
})

const readDimensions = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file)
  const img = new Image()
  img.onload = () => {
    resolve({width: img.naturalWidth, height: img.naturalHeight})
    URL.revokeObjectURL(url)
  }
  img.onerror = () => {
    resolve(null)
    URL.revokeObjectURL(url)
  }
  img.src = url
})

const validate = async (file) => {
  if (file.size > MAX_BYTES) {
    return 'Cover must be under 2 MB.'
  }
  const size = await readDimensions(file)
  if (!size) return null
  if (size.width < MIN_WIDTH || size.height < MIN_HEIGHT) {
    return `Cover must be at least ${MIN_WIDTH}×${MIN_HEIGHT}. This image is ${size.width}×${size.height}.`
  }
  return null
}

const run = async (action, successTitle, errorTitle) => {
  inProgress.value = true
  try {
    await action()
    toast({title: successTitle})
  } catch (e) {
    toast({title: e.response?.data?.detail || errorTitle, variant: 'destructive'})
  } finally {
    inProgress.value = false
  }
}

const onFileSelected = async (event) => {
  const file = event.target.files[0]
  event.target.value = ''
  if (!file) return

  const error = await validate(file)
  if (error) {
    toast({title: error, variant: 'destructive'})
    return
  }
  await run(() => projectStore.uploadProjectCover({project: props.project, file}), 'Cover updated', 'Can\'t upload cover. Try again.')
}

const onTypeChange = async (value) => {
  if (value === coverType.value) return
  if (value === 'image' && !props.project.cover_url) {
    fileInput.value.click()
    return
  }
  await run(() => projectStore.setProjectCoverType({project: props.project, coverType: value}), 'Cover updated', 'Can\'t change cover. Try again.')
}

const removeImage = () => run(() => projectStore.removeProjectCover(props.project), 'Cover image removed', 'Can\'t remove cover. Try again.')
</script>

<template>
  <div class="space-y-2">
    <Label for="project-cover-type">Cover</Label>
    <Select :model-value="coverType" :disabled="disabled || inProgress" @update:model-value="onTypeChange">
      <SelectTrigger id="project-cover-type" class="w-56">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="map">Map preview</SelectItem>
        <SelectItem value="emoji" :disabled="!project.emoji">Emoji</SelectItem>
        <SelectItem value="image">Image</SelectItem>
      </SelectContent>
    </Select>

    <div class="relative aspect-video w-56 overflow-hidden rounded-md border bg-muted">
      <img
        v-if="coverType === 'image' && project.cover_url"
        :src="project.cover_url"
        :alt="`Cover of ${project.project_name}`"
        class="w-full h-full object-cover"
      />
      <div v-else-if="coverType === 'emoji' && project.emoji" class="w-full h-full flex items-center justify-center">
        <span class="text-5xl leading-none">{{ project.emoji }}</span>
      </div>
      <img
        v-else-if="mapUrl"
        :src="mapUrl"
        :alt="`Map preview of ${project.project_name}`"
        class="w-full h-full object-cover"
      />
      <div v-else class="w-full h-full flex items-center justify-center text-muted-foreground">
        <Folder class="w-6 h-6" />
      </div>
      <div v-if="inProgress" class="absolute inset-0 flex items-center justify-center bg-background/60">
        <Spinner class="w-5 h-5" />
      </div>
    </div>

    <template v-if="disabled">
      <p class="text-xs text-muted-foreground">Only project admins can change the cover.</p>
    </template>
    <template v-else>
      <template v-if="coverType === 'image'">
        <p class="text-xs text-muted-foreground">Any image up to 2 MB. Cropped to fit the card.</p>
        <div class="flex gap-2">
          <Button type="button" size="sm" variant="outline" :disabled="inProgress" @click="fileInput.click()">
            <ImageUp class="w-4 h-4 mr-2" />
            {{ project.cover_url ? 'Replace' : 'Upload' }}
          </Button>
          <Button v-if="project.cover_url" type="button" size="sm" variant="ghost" class="text-destructive hover:text-destructive" :disabled="inProgress" @click="removeImage">
            <Trash2 class="w-4 h-4 mr-2" />
            Remove
          </Button>
        </div>
      </template>
      <input ref="fileInput" type="file" class="hidden" :accept="accept" @change="onFileSelected" />
    </template>
  </div>
</template>
